import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";

import TableListMaintenanceItem from "@/shared/components/organisms/TableListMaintenanceItem";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import TableListInventory from "@/shared/components/organisms/TableListInventory";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import PaginationUI from "@/shared/components/atoms/PaginationUI";
import { ModalXXL } from "@/shared/components/organisms/ModalXXL";
import { Select } from "@/shared/components/organisms/Select";
import { Modal } from "@/shared/components/organisms/Modal";
import { toasts } from "@/shared/utils/toasts";

import {
  useCreateInventoryItemMutation,
  useCreateInventoryMutation,
  useDeleteInventoryItemMutation,
  useInventoriesQuery,
  useInventoryItemsQuery,
  useUpdateInventoryItemMutation,
} from "@/app/inventory/inventory.api";
import { useMaintenanceItemsQuery } from "@/app/maintenance/maintenance.api";

import {
  inventoryInitialValue,
  inventoryItemInitialValue,
  inventoryItemValidationSchema,
  inventoryValidationSchema,
} from "@/shared/schemas/inventories.schema";
import { Permission, usePermission } from "@/shared/hooks/usePermission";

const PAGE_SIZE = 10;

export const Inventory: FC<any> = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [currentInventoryItem, setCurrentInventoryItem] = useState<any>();
  const { can } = usePermission();
  // Controlar Abre/Cierre del Alert para confirmación de eliminar
  const [openDelete, setOpenDelete] = useState(false);
  // Estado para el ID del gimnasio a eliminar
  const [inventoryToDelete, setInventoryToDelete] = useState<
    string | number | null
  >(null);

  const inventoriesQuery = useInventoriesQuery({ branchId: id });

  const [createInventory] = useCreateInventoryMutation();
  const [updateInventoryItem, updateInventoryItemResult] =
    useUpdateInventoryItemMutation();
  const [deleteInventoryItem, deleteInventoryItemResult] =
    useDeleteInventoryItemMutation();
  const [createInventoryItem, createInventoryItemResult] =
    useCreateInventoryItemMutation();

  useEffect(() => {
    if (deleteInventoryItemResult.isSuccess) {
      toasts.success(
        "Eliminación exitosa",
        "El inventario se ha eliminado correctamente"
      );
    }
    if (deleteInventoryItemResult.isError) {
      toasts.success(
        "Error al eliminar",
        "El inventario no se ha podido eliminar"
      );
    }
  }, [deleteInventoryItemResult]);

  const [currentInventoryItemEdit, setCurrentInventoryItemEdit] =
    useState<any>();

  // Estados para paginación
  const [page, setPage] = useState(1);

  const maintenanceItemsQuery = useMaintenanceItemsQuery(
    { inventoryItemId: currentInventoryItem?.id, branchId: id },
    { skip: !currentInventoryItem }
  );
  const inventoryItemsQuery = useInventoryItemsQuery({
    branchId: id,
    inventoryId: category?.id,
    page,
    pageSize: PAGE_SIZE,
  });

  console.log(inventoryItemsQuery);

  // Resetear página cuando cambie la categoría
  useEffect(() => {
    setPage(1);
  }, [category]);

  //CATEGORIAS
  //STATE PARA MANEJAR OPEN/CLOSE DE LOS FILTROS
  const [filters, setFilters] = useState(false);
  const filtersOpen = () => {
    setFilters(!filters);
  };
  //OPEN FOR DIALOG CREATE
  const [open, setOpen] = useState(false);
  const openDialog = () => {
    if (inventoriesQuery?.data?.length === 0) {
      toasts.warning(
        "No hay categorías",
        "No se puede crear un inventario sin categorías. Por favor cree una categoría"
      );
      return;
    }

    setOpen(!open);
  };
  //OPEN MODAL
  const [openModal, setOpenModal] = useState(false);
  const openModalCreate = () => {
    setOpenModal(!openModal);
  };
  //OPEN MODAL TABLE TASKS
  const [openModalTasks, setOpenModalTasks] = useState(false);
  const openModalTableTasks = () => {
    setOpenModalTasks(!openModalTasks);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleOnClose = () => {
    setCurrentInventoryItemEdit(undefined);
  };

  const handleOpenDelete = (id: string | number | null) => {
    setInventoryToDelete(id);
    setOpenDelete(!openDelete);
  };

  async function handleCreateInventory(values: any) {
    try {
      await createInventory({
        name: values.name,
        branchId: id,
      }).unwrap();
      toasts.success(
        "Registro exitoso",
        "El inventario se ha registrado correctamente"
      );
      inventoriesQuery.refetch();
    } catch (error) {
      console.error("Error on handleCreateInventory", error);
      toasts.danger(
        "Error al registrar",
        "Ha ocurrido un error al registrar el inventario"
      );
    }
  }

  async function handleUpdateInventory(values: any) {
    try {
      await updateInventoryItem({
        ...values,
        id: currentInventoryItemEdit.id,
        branchId: id,
      }).unwrap();
      toasts.success(
        "Actualización exitosa",
        "El inventario se ha actualizado correctamente"
      );
      setCurrentInventoryItemEdit(undefined);
      inventoryItemsQuery.refetch();
    } catch (error) {
      console.error("Error on handleUpdateInventory", error);
      toasts.danger(
        "Error al actualizar",
        "Ha ocurrido un error al actualizar el inventario"
      );
    }
  }

  async function handleCreateInventoryItem(values: any) {
    try {
      await createInventoryItem({ ...values, branchId: id }).unwrap();
      toasts.success(
        "Registro exitoso",
        "El inventario se ha registrado correctamente"
      );
      inventoryItemsQuery.refetch();
    } catch (error) {
      console.error("Error on handleCreateInventoryItem", error);
      toasts.danger(
        "Error al crear",
        "Ha ocurrido un error al crear el inventario"
      );
    }
  }

  return (
    <div className="flex flex-col size-full mt-8">
      <div className="flex-1 flex flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full mx-auto">
        <TableListInventory
          isLoading={inventoryItemsQuery.isLoading}
          title={t("inventory.views.tittle")}
          description={t("inventory.views.description")}
          inventarios={inventoryItemsQuery.data?.data ?? []}
          functionButtonCreate={openDialog}
          canCreateInventory={can(Permission.BRANCH_ACCESS_CREATE_INVENTORY)}
          canEditInventory={can(Permission.BRANCH_ACCESS_EDIT_INVENTORY)}
          canDeleteInventory={can(Permission.BRANCH_ACCESS_DELETE_INVENTORY)}
          filterButton={filtersOpen}
          functionButtonCreateCategory={openModalCreate}
          optionsCategory={inventoriesQuery?.data ?? []}
          category={category}
          setCategory={setCategory}
          onViewTasks={(item) => {
            setCurrentInventoryItem(item);
            setOpenModalTasks(true);
          }}
          onEdit={(inventoryItem) => {
            setCurrentInventoryItemEdit(inventoryItem);
            setOpen(true);
          }}
          onDelete={(inventoryItem) => handleOpenDelete(inventoryItem.id)}
        />
        {inventoryItemsQuery.data && (
          <PaginationUI
            currentPage={page}
            totalPages={Math.ceil(
              (inventoryItemsQuery.data?.total ?? 0) / PAGE_SIZE
            )}
            onPageChange={handlePageChange}
            pageSize={PAGE_SIZE}
            totalResults={inventoryItemsQuery.data?.total ?? 0}
          />
        )}

        <AlertErrorButtons
          open={openDelete}
          onClose={() => handleOpenDelete(null)}
          title={t("maintenance.views.delete_item_confirmation_title")}
          message={t("maintenance.views.delete_item_confirmation_message")}
          primaryButtonText={t("general.delete")}
          secondaryButtonText={t("general.cancel")}
          onPrimaryButtonClick={() => {
            deleteInventoryItem({ branchId: id, id: inventoryToDelete });
            handleOpenDelete(null);
          }}
          onSecondaryButtonClick={() => handleOpenDelete(null)}
        />

        {/* MODAL FOT THE TASKS */}
        <ModalXXL
          open={openModalTasks}
          Close={() => {
            openModalTableTasks();
            setCurrentInventoryItem(undefined);
          }}
          tittleModal={t("lead.views.tasks")}
          textButtonClose={t("general.close")}
        >
          <TableListMaintenanceItem
            onClickEmpty={() => navigate(`/branchs/${id}/maintenance`)}
            maintenanceItems={maintenanceItemsQuery?.data ?? []}
          />
        </ModalXXL>

        {/* FORMULARIO PARA CREAR INVENTARIOS */}
        <Formik
          enableReinitialize
          onSubmit={async (values, formikHelpers) => {
            try {
              if (currentInventoryItemEdit) {
                await handleUpdateInventory(values);
                formikHelpers.resetForm();
                setOpen(false);
              } else {
                /* createInventoryItem({
                  ...values,
                  branchId: id,
                }); */
                await handleCreateInventoryItem(values);
                formikHelpers.resetForm();
                setOpen(false);
              }
            } catch (error) {
              console.error("Error on handleUpdateInventory", error);
            }
          }}
          initialValues={
            currentInventoryItemEdit
              ? {
                  name: currentInventoryItemEdit.name,
                  nro: currentInventoryItemEdit.nro,
                  inventoryId: currentInventoryItemEdit.inventory_id,
                }
              : inventoryItemInitialValue
          }
          validationSchema={inventoryItemValidationSchema}
        >
          {({
            handleChange,
            handleSubmit,
            values,
            errors,
            touched,
            setFieldValue,
            resetForm,
          }) => (
            <DialogCreate
              OnclickCreate={handleSubmit}
              OnclickCancel={() => {
                setOpen(false);
                resetForm();
                setCurrentInventoryItemEdit(undefined);
              }}
              tittleButtonCreate={
                currentInventoryItemEdit
                  ? t("general.edit")
                  : t("general.create")
              }
              onClose={() => {
                setOpen(false);
                resetForm();
                setCurrentInventoryItemEdit(undefined);
              }}
              tittleButtonCancel={t("general.cancel")}
              open={open}
              setOpen={setOpen}
              acceptDisabled={
                createInventoryItemResult.isLoading ||
                updateInventoryItemResult.isLoading
              }
              tittle={
                currentInventoryItemEdit
                  ? t("inventory.views.edit_item")
                  : t("inventory.views.create_item")
              }
              subTittle={t("inventory.views.subtittle_item")}
              isLoading={
                createInventoryItemResult.isLoading ||
                updateInventoryItemResult.isLoading
              }
            >
              <form action="">
                <div className="mt-6 mx-2">
                  <div className="space-y-12">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                      <div className="flex items-center justify-center h-full text-center">
                        <div>
                          <h2 className="text-base font-semibold leading-7 text-gray-900">
                            {t("inventory.views.item")}
                          </h2>
                        </div>
                      </div>
                      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="col-span-full">
                          <Select
                            addDefault={false}
                            name="inventoryId"
                            span={t("general.mandatory")}
                            options={inventoriesQuery.data}
                            setSelected={(option) =>
                              setFieldValue("inventoryId", option.id)
                            }
                            selected={inventoriesQuery.data?.find(
                              (inventory) => inventory.id == values.inventoryId
                            )}
                            tittle={t("inventory.views.category")}
                            error={
                              errors.inventoryId &&
                              (touched.inventoryId as boolean)
                            }
                            textError={errors.inventoryId as string}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("inventory.views.name")}
                            span={t("general.mandatory")}
                            name="name"
                            type="text"
                            placeholder={t("inventory.views.name")}
                            onChange={handleChange}
                            error={errors.name && (touched.name as boolean)}
                            textError={errors.name}
                            value={values.name}
                          />
                        </div>
                        <div className="col-span-full">
                          <InputWithCornerHint
                            label={t("inventory.views.reference")}
                            span={t("general.mandatory")}
                            name="nro"
                            type="text"
                            placeholder={t("inventory.views.reference")}
                            value={values.nro}
                            onChange={handleChange}
                            error={errors.nro && (touched.nro as boolean)}
                            textError={errors.nro}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </DialogCreate>
          )}
        </Formik>

        {/* FORMULARIO PARA CREAR CATEGORIAS */}
        <Formik
          onSubmit={async (values, formikHelpers) => {
            try {
              await handleCreateInventory({
                name: values.name,
                branchId: id,
              });
              setOpenModal(false);
              formikHelpers.resetForm();
            } catch (error) {
              console.error("Error on handleCreateInventory", error);
            }
          }}
          validationSchema={inventoryValidationSchema}
          initialValues={inventoryInitialValue}
        >
          {({ handleChange, handleSubmit, values, errors, resetForm }) => (
            <Modal
              Close={() => {
                setOpenModal(false);
                resetForm();
              }}
              open={openModal}
              onClickCreate={handleSubmit}
              textButtonCreate={t("general.create")}
              textButtonClose={t("general.cancel")}
              tittleModal={t("inventory.views.new_category")}
            >
              <div className="w-full">
                <div className="w-full mt-3">
                  <InputWithCornerHint
                    label={t("inventory.views.category")}
                    name="name"
                    type="text"
                    placeholder={t("inventory.views.category")}
                    onChange={handleChange}
                    error={!!errors.name}
                    textError={errors.name}
                    value={values.name}
                  />
                </div>
              </div>
            </Modal>
          )}
        </Formik>
      </div>
    </div>
  );
};
